<template>
  <div class="unit-commander-index-container">
    <Sidenav
      v-if="showFilters"
      :is-sidebar-active.sync="isSidebarActive"
      :is-add="isAdd"
      :form-data="formData"
      @submit="onSubmit"
      @hidden="resetForm"
    />

    <Transition name="slide-right-left">
      <div v-if="!showFilters && isFormPanelOpen" class="filters-panel-fullscreen">
        <div class="filters-panel-header">
          <p class="h4 text-capitalize mb-2">
            {{ (isAdd ? $t('actions.add') : $t('actions.edit')) + ' ' + $t('patrol_logs.unit_commander.title') }}
          </p>
          <b-button variant="link" size="sm" @click="closeFormPanel" class="text-dark p-0">
            <feather-icon icon="XIcon" size="24" />
          </b-button>
        </div>
        <div class="filters-panel-content pt-2">
          <unit-commander-form
            :form-data="formData"
            :is-add="isAdd"
            @submit="onSubmit"
            @reset="closeFormPanel"
          />
        </div>
      </div>
    </Transition>

    <b-row v-if="showFilters" class="pt-2 pb-1">
      <b-col cols="6" class="d-flex align-items-center">
        <BreadcrumbMenu />
      </b-col>
      <b-col cols="6" class="pr-3 text-right">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <filter-modal
      @action="buildFilters()"
      @clear="clearSearchFilters()"
      v-if="showFilters"
    >
      <template v-slot:main-filters>
        <Filters />
      </template>
    </filter-modal>

    <toastification-loading-content ref="loadingToast" />

    <Transition name="slide-right-left">
      <div v-if="!showFilters && isFilterPanelOpen" class="filters-panel-fullscreen">
        <div class="filters-panel-header">
          <p class="h4 text-capitalize mb-2">{{ $t('messages.info.advanced_search') }}</p>
          <b-button variant="link" size="sm" @click="isFilterPanelOpen = false" class="text-dark p-0">
            <feather-icon icon="XIcon" size="24" />
          </b-button>
        </div>
        <div class="filters-panel-content">
          <Filters />
        </div>
        <div class="filters-panel-footer">
          <b-button variant="outline-primary" size="sm" @click="clearSearchFilters()" class="text-capitalize">
            {{ $t('messages.info.clear_filters') }}
          </b-button>
          <b-button variant="primary" size="sm" @click="applyFilters()" class="text-capitalize">
            {{ $t('search') }}
            <feather-icon icon="SearchIcon" />
          </b-button>
        </div>
      </div>
    </Transition>

    <b-card-group
      v-show="showFilters || (!isFilterPanelOpen && !isFormPanelOpen)"
      deck
      :class="{ 'mx-2': showFilters, 'm-0': !showFilters }"
    >
      <Transition name="slide-left-right">
        <b-card class="m-0" no-body>
          <div class="m-2">
            <b-row>
              <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0" v-if="showFilters">
                <label class="text-capitalize">{{ $t('table.show') }}</label>
                <v-select
                  v-model="perPage"
                  :options="perPageOptions"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :clearable="false"
                  class="per-page-selector d-inline-block mx-50"
                />
                <label class="text-capitalize">{{ $t('table.entries') }}</label>
              </b-col>

              <b-col cols="12" :md="showFilters ? '6' : '12'">
                <div class="d-flex align-items-center justify-content-end" :class="{ 'w-100': !showFilters }">
                  <b-form-input
                    v-model="searchQuery"
                    :class="showFilters ? 'd-inline-block mr-1' : 'd-inline-block mr-1 w-75'"
                    :placeholder="$t('table.search')"
                  />

                  <b-button-group v-if="!showFilters">
                    <b-button @click="isFilterPanelOpen = true" class="buttonFilter cursor-pointer d-flex align-items-center">
                      <b-icon-filter-circle-fill scale="1.1" />
                      <span class="align-middle ml-1 text-capitalize">{{ $t('filters') }}</span>
                    </b-button>
                    <toggle-clear-filter @clear-search-filters="clearSearchFilters" :filters-count="filtersCount" />
                  </b-button-group>

                  <b-button v-if="$can('create', 'UnitCommanders')" variant="secondary" @click="openAdd()" :class="!showFilters ? 'ml-1' : ''">
                    <span class="text-nowrap text-capitalize">
                      {{ $t('actions.add') + ' ' + $t('patrol_logs.unit_commander.title') }}
                    </span>
                  </b-button>

                  <b-button
                    @click="exportExcel()"
                    variant="success"
                    class="text-capitalize ml-1"
                    type="button"
                    :disabled="buttonExport"
                  >
                    <feather-icon icon="DownloadCloudIcon" size="15" />
                  </b-button>
                </div>
              </b-col>
            </b-row>
          </div>

          <b-table
            ref="refListTable"
            class="position-relative"
            :items="fetchList"
            responsive
            :fields="tableColumns"
            primary-key="id"
            :sort-by.sync="sortBy"
            show-empty
            :empty-text="$t('no_record_found')"
            :sort-desc.sync="isSortDirDesc"
          >
            <template #head()="data">
              <span> {{ $tc(data.label, 1) }}</span>
            </template>
            <template #head(actions)="data">
              <span> {{ $tc(data.label, 2) }}</span>
            </template>

            <template #cell(actions)="data">
              <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
                <template #button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="16"
                    class="align-middle text-body"
                  />
                </template>

                <b-dropdown-item @click="openEdit(data.item)"
                  v-if="$can('update', 'UnitCommanders')">
                  <feather-icon icon="EditIcon" />
                  <span class="align-middle ml-50 text-capitalize">{{ $t('actions.edit') }}</span>
                </b-dropdown-item>

                <b-dropdown-item @click="confirmDelete(data.item.id)"
                  v-if="$can('delete', 'UnitCommanders')">
                  <feather-icon icon="TrashIcon" />
                  <span class="align-middle ml-50 text-capitalize">{{ $t('actions.delete') }}</span>
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>

          <div class="mx-2 mb-2">
            <b-row>
              <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
                <span class="text-muted text-capitalize">
                  {{ $t('table.showing', { from: dataMeta.from, to: dataMeta.to, of: dataMeta.of }) }}
                </span>
              </b-col>
              <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
                <Pagination
                  :total-list="totalList"
                  :per-page="perPage"
                  :current-page="currentPage"
                  @updatePagination="currentPage = $event"
                />
              </b-col>
            </b-row>
          </div>
        </b-card>
      </Transition>
    </b-card-group>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ToastificationLoadingContent from '@core/components/toastification/ToastificationLoadingContent.vue'
import { useUtils as useI18nUtils } from '@core/libs/i18n'
import store from '@/store'
import vSelect from 'vue-select'
import BreadcrumbMenu from '@/components/BreadcrumbMenu.vue'
import FilterModal from '@/components/Filters/FilterModal.vue'
import ButtonFilters from '@/components/Filters/ButtonFilters.vue'
import ToggleClearFilter from '@/components/Filters/ToggleClearFilter.vue'
import Pagination from '@/components/Pagination.vue'
import Filters from './Filters.vue'
import Sidenav from './Sidenav.vue'
import UnitCommanderForm from './Form.vue'
import useList from './useList'

export default {
  components: {
    BreadcrumbMenu,
    ButtonFilters,
    ToggleClearFilter,
    FilterModal,
    Pagination,
    Filters,
    Sidenav,
    UnitCommanderForm,
    vSelect,
    ToastificationLoadingContent,
  },
  props: {
    showFilters: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { root }) {
    const toast = useToast()
    const { t } = useI18nUtils()
    const trans = {
      'messages.error.server_error': t('messages.error.server_error'),
      'messages.delete.title': t('messages.delete.title'),
      'messages.delete.body': t('messages.delete.body'),
      'actions.delete': t('actions.delete'),
      'actions.cancel': t('actions.cancel'),
      'messages.export.download': t('messages.export.download'),
      'messages.export.Generating_file': t('messages.export.Generating_file'),
    }

    const loadingToast = ref(null)
    const buttonExport = ref(false)

    const isSidebarActive = ref(false)
    const isAdd = ref(false)
    const isFilterPanelOpen = ref(false)
    const isFormPanelOpen = ref(false)

    const blankForm = {
      id: null,
      name: '',
      telephone_number: '',
    }
    const formData = ref({ ...blankForm })

    const {
      refListTable,
      tableColumns,
      fetchList,
      perPage,
      currentPage,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      totalList,
      dataMeta,
      refetchData,
      filtersCount,
      buildFilters,
      clearSearchFilters,
      buildListQueryParams,
    } = useList()

    const applyFilters = () => {
      buildFilters()
      isFilterPanelOpen.value = false
    }

    const exportExcel = () => {
      buttonExport.value = true
      loadingToast.value.show(
        trans['messages.export.download'],
        trans['messages.export.Generating_file'],
        'secondary'
      )
      store
        .dispatch('unit_commander/export', {
          ...buildListQueryParams(),
          action: 'export',
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: trans['messages.error.server_error'],
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          loadingToast.value.close()
          buttonExport.value = false
        })
    }

    const openAdd = () => {
      isAdd.value = true
      formData.value = { ...blankForm }
      if (props.showFilters) {
        isSidebarActive.value = true
      } else {
        isFormPanelOpen.value = true
      }
    }

    const openEdit = (item) => {
      isAdd.value = false
      formData.value = {
        id: item.id,
        name: item.name,
        telephone_number: item.telephone_number || '',
      }
      if (props.showFilters) {
        isSidebarActive.value = true
      } else {
        isFormPanelOpen.value = true
      }
    }

    const resetForm = () => {
      formData.value = { ...blankForm }
      isSidebarActive.value = false
    }

    const closeFormPanel = () => {
      formData.value = { ...blankForm }
      isFormPanelOpen.value = false
    }

    const onSubmit = () => {
      if (isAdd.value) {
        store.dispatch('unit_commander/add', formData.value)
          .then(response => {
            if (response.success) {
              toast({
                component: ToastificationContent,
                props: {
                  title: response.message,
                  icon: 'CheckIcon',
                  variant: 'success',
                },
              })
              refetchData()
            }
            isSidebarActive.value = false
            isFormPanelOpen.value = false
          })
          .catch(() => {
            toast({
              component: ToastificationContent,
              props: {
                title: trans['messages.error.server_error'],
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          })
      } else {
        store.dispatch('unit_commander/edit', { id: formData.value.id, params: formData.value })
          .then(response => {
            if (response.success) {
              toast({
                component: ToastificationContent,
                props: {
                  title: response.message,
                  icon: 'CheckIcon',
                  variant: 'success',
                },
              })
              refetchData()
            }
            isSidebarActive.value = false
            isFormPanelOpen.value = false
          })
          .catch(() => {
            toast({
              component: ToastificationContent,
              props: {
                title: trans['messages.error.server_error'],
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            })
          })
      }
    }

    const confirmDelete = (id) => {
      root.$bvModal
        .msgBoxConfirm(trans['messages.delete.body'], {
          title: trans['messages.delete.title'],
          okVariant: 'danger',
          okTitle: trans['actions.delete'],
          cancelTitle: trans['actions.cancel'],
          centered: true,
        })
        .then((confirmed) => {
          if (confirmed) {
            store.dispatch('unit_commander/delete', id)
              .then(response => {
                if (response.success) {
                  toast({
                    component: ToastificationContent,
                    props: {
                      title: response.message,
                      icon: 'CheckIcon',
                      variant: 'success',
                    },
                  })
                  refetchData()
                }
              })
              .catch(() => {
                toast({
                  component: ToastificationContent,
                  props: {
                    title: trans['messages.error.server_error'],
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                })
              })
          }
        })
    }

    return {
      isSidebarActive,
      isAdd,
      formData,
      resetForm,
      closeFormPanel,
      onSubmit,
      openAdd,
      openEdit,
      confirmDelete,
      isFormPanelOpen,

      refListTable,
      tableColumns,
      fetchList,
      perPage,
      currentPage,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      totalList,
      dataMeta,
      refetchData,

      isFilterPanelOpen,
      filtersCount,
      buildFilters,
      clearSearchFilters,
      applyFilters,
      exportExcel,
      loadingToast,
      buttonExport,
    }
  },
}
</script>

<style lang="scss">
@import "@/assets/scss/utilities/transitions";

.unit-commander-index-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.filters-panel-fullscreen {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  z-index: 10;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.filters-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 0;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.filters-panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 0 1.5rem;
}

.filters-panel-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  flex-shrink: 0;
}

.dark-layout {
  .filters-panel-fullscreen {
    background-color: #283046;
    color: #b4b7bd;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  .filters-panel-header {
    border-bottom-color: rgba(255, 255, 255, 0.05);
  }

  .filters-panel-footer {
    border-top-color: #3b4253;
  }
}
</style>
